<template>
  <div>
    <z-header :title="title" :actions="actions"></z-header>
    <AddressWarning />
    <b-notification
      type="is-warning"
      aria-close-label="Close notification"
      :closable="false"
      v-if="hasPendingOrder"
    >
      Você tem um
      <router-link to="/compras" class="is-text">pedido pendente</router-link>.
    </b-notification>
    <form
      @submit.prevent="validateBeforeSubmit"
      v-bind:class="{
        disable: hasPendingOrder,
      }"
    >
      <div
        class="group-form"
        v-bind:class="{
          bankslip: data.paymentMethod === 'bankslip',
        }"
      >
        <b-field
          label="Forma de pagamento"
          size="is-small"
          :type="{ 'is-danger': errors.has('paymentMethod') }"
          :message="
            (errors.first('paymentMethod') || '').replace(
              'paymentMethod',
              'forma de pagamento'
            )
          "
        >
          <b-select
            name="paymentMethod"
            expanded
            v-model="data.paymentMethod"
            v-validate="'required'"
          >
            <option
              v-for="method in paymentMethods"
              :value="method.value"
              :key="method.value"
            >
              {{ method.label }}
            </option>
          </b-select>
        </b-field>

        <b-field
          label="Valor"
          expanded
          :type="{ 'is-danger': errors.has('total') }"
          :message="errors.first('total')"
          size="is-merdium"
          v-if="!!data.paymentMethod"
        >
          <b-input
            name="total"
            v-model="data.total"
            v-validate="'required'"
            @input="currencyMask"
          />
        </b-field>
        <b-field
          label="Data de vencimento"
          v-if="data.paymentMethod === 'bankslip'"
        >
          <div class="custom-datepicker">
            <b-datepicker
              v-model="dueDate"
              placeholder="Escolha a data de vencimento"
              :horizontal-time-picker="false"
              :min-date="minDatetime"
              :max-date="maxDatetime"
              :locale="'pt-BR'"
              :day-names="['D', 'S', 'T', 'Q', 'Q', 'S', 'S']"
              :month-names="[
                'Janeiro',
                'Fevereiro',
                'Março',
                'Abril',
                'Maio',
                'Junho',
                'Julho',
                'Agosto',
                'Setembro',
                'Outubro',
                'Novembro',
                'Dezembro',
              ]"
            >
            </b-datepicker>
          </div>
        </b-field>
      </div>
      <hr />
      <div class="buttons is-right">
        <button
          class="button is-rounded is-primary"
          type="submit"
          @click.prevent="validateAndSubmit"
          :disabled="hasPendingOrder || isLoading"
        >
          Enviar
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import ZHeader from "@/layout/Header";
import orders from "../../services/orders";
import AddressWarning from "../../components/AddressWarning";

import { format } from "date-fns";

export default {
  data() {
    const min = new Date();
    const max = new Date();

    min.setDate(min.getDate() - 1);

    max.setDate(max.getDate() + 30);

    return {
      PIX: "pix",
      title: "Novo pedido de compra",
      actions: [
        {
          label: "Voltar",
          route: "/compras",
        },
      ],
      paymentMethods: [
        {
          value: "bankslip",
          label: "Boleto",
        },
        {
          value: "pix",
          label: "Pix",
        },
        {
          value: "",
          label: "Selecione um método",
        },
      ],
      data: {
        paymentMethod: "",
      },
      isLoading: false,
      amountPresets: [],
      products: [],
      hasPendingOrder: false,
      minDatetime: min,
      maxDatetime: max,
      dueDate: new Date(),
    };
  },
  async mounted() {
    const isPending = await this.checkIfHasPendingOrder();

    if (!isPending) {
      orders.getProducts().then((products) => {
        this.products = products;

        this.amountPresets = Object.keys(products).map((a) => ({
          value: a,
          title: products[a],
        }));
      });
    }

    this.hasPendingOrder = isPending;
  },
  methods: {
    currencyMask(value) {
      let rawValue = value.replace(".", "").replace(",", "").replace(/\D/g, "");

      if (rawValue.length === 0) {
        rawValue = "0,00";
      }

      const formattedValue = new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
      }).format(parseFloat(rawValue) / 100);

      this.data.total = `R$ ${formattedValue.replace("BLR", "").trim()}`;
    },
    async checkIfHasPendingOrder() {
      const orderList = await orders.all();
      const pendingOrder = (orderList || [])
        .slice()
        .reverse()
        .find((order) => order.status === "PENDING");

      try {
        return !!pendingOrder;
      } catch {
        return false;
      }
    },
    getRawData() {
      const rawData = {
        total: this.data.total,
        paymentMethod: this.data.paymentMethod,
      };

      if (this.data.paymentMethod === "bankslip") {
        rawData.dueDate = format(this.dueDate, "yyyy-MM-dd");
      }

      rawData.total = parseFloat(
        rawData.total
          .replace("R$", "")
          .replace(/\s/g, "")
          .replace(".", "")
          .replace(",", ".")
      );

      return rawData;
    },
    validateAndSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.save(this.getRawData());
          return;
        }
        this.$buefy.toast.open({
          message: "Verfique os campos marcados para continuar",
          type: "is-danger",
        });
      });
    },
    async save(rawData) {
      try {
        this.isLoading = true;
        this.$buefy.dialog.confirm({
          message: `Você confirma a compra de <strong>${this.data.total}</strong> em créditos?`,
          onConfirm: async () => {
            const { total, dueDate, paymentMethod } = rawData;

            const newOrder = await orders.create(
              { total, dueDate },
              paymentMethod
            );
            localStorage.setItem("newOrder", newOrder.id);

            this.$router.push({
              name: "orders",
            });
          },
          onCancel: () => (this.isLoading = false),
        });
      } catch (err) {
        console.error(err);
        this.isLoading = false;
        this.$buefy.toast.open({
          message: "Erro ao atualizar as configurações",
          type: "is-danger",
        });
      }
    },
  },
  components: {
    ZHeader,
    AddressWarning,
  },
};
</script>

<style lang="scss" scoped>
.group-form {
  display: flex;
  flex-direction: column;

  > .field {
    width: 100%;
  }

  &:not(.bankslip) {
    @media screen and (min-width: 600px) {
      flex-direction: row;

      > .field:first-child {
        width: calc(60% - 20px);
        margin-right: 20px;
      }

      > .field:last-child {
        width: 40%;
      }
    }
  }

  &.bankslip {
    @media screen and (min-width: 600px) {
      flex-direction: row;
      gap: 20px;
    }
  }
}

form.disable {
  opacity: 0.6;
  user-select: none;
  pointer-events: none;
}
</style>
